import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FAST_SWIPE_MS, SHORT_CLIP_MS, VIEW_THRESHOLD_MS } from '@/constants/contentFeed';
import { recordFocusedView } from '@/services/api/requests/content';
import { TContent, TRecordFocusedViewPayload } from '@/types/Content.types';


const thresholdFor = (content: TContent, durationSeconds?: number | null): number => {
  if (content.type !== 'video' || !durationSeconds) return VIEW_THRESHOLD_MS;

  const durationMs = durationSeconds * 1_000;

  return durationMs < SHORT_CLIP_MS ? Math.min(VIEW_THRESHOLD_MS, durationMs / 2) : VIEW_THRESHOLD_MS;
};

const playbackSourceFor = (content: TContent): TRecordFocusedViewPayload['playbackSource'] => {
  if (content.type !== 'video') return 'none';
  const source = (content.details as { source?: string } | undefined)?.source;

  return source === 'youtube' ? 'youtube' : source === 'mux' ? 'mux' : 'none';
};

const createSessionId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `loop-${Date.now()}-${Math.random().toString(36).slice(2)}`;

type TActiveItem = {
  content: TContent;
  // Wall-clock, used for the fast-swipe signal and as the dwell figure reported.
  focusedAt: number;
  // What the threshold is measured against: elapsed PLAYBACK for video, elapsed
  // on-screen time for articles and posts.
  qualifyingMs: number;
  // Set while the qualifying clock is running (video playing / item on screen).
  runningSince: number | null;
  timer: number | null;
  completionPct?: number;
};

// Reports genuine focused-mode consumption — the ONLY signal that counts as a view.
//
// The distinction that matters: for video, the clock is PLAYBACK time, not time on
// screen. An item can sit focused indefinitely without ever being played (autoplay is
// blocked, the user never pressed play, the embed failed), and counting that as a view
// would inflate view counts with things nobody watched. Articles and posts have no
// playback, so for those the clock is on-screen time — which is the real signal there.
//
// Other discipline: fetching an item is not a view, so prefetched-but-unseen items emit
// nothing; the event fires at the moment the bar is crossed rather than on unmount, so a
// closed tab does not lose it; and each item reports at most once per loop session.
const useFocusedViewTracker = () => {
  const sessionIdRef = useRef<string>(createSessionId());
  const reportedRef = useRef<Set<string>>(new Set());
  const activeRef = useRef<TActiveItem | null>(null);

  const { mutate } = useMutation({
    mutationFn: (payload: TRecordFocusedViewPayload) => recordFocusedView(payload),
    // Telemetry must never interrupt playback; a dropped view is acceptable, a thrown
    // error mid-loop is not.
    onError: () => {},
  });

  const clearTimer = () => {
    const active = activeRef.current;
    if (active?.timer) {
      window.clearTimeout(active.timer);
      active.timer = null;
    }
  };

  const report = useCallback(
    (item: TActiveItem, swipedAwayFast: boolean) => {
      const { content } = item;
      if (reportedRef.current.has(content.id)) return;
      reportedRef.current.add(content.id);

      const source = playbackSourceFor(content);

      mutate({
        contentId: content.id,
        sessionId: sessionIdRef.current,
        dwellMs: Math.round(Date.now() - item.focusedAt),
        playbackSource: source,
        swipedAwayFast,
        // Only sent where it is actually measurable — the backend expects it omitted for
        // YouTube embeds and for articles/posts.
        ...(source === 'mux' && item.completionPct !== undefined
          ? { completionPct: Math.round(item.completionPct) }
          : {}),
      });
    },
    [mutate],
  );

  // Accumulate whatever the qualifying clock has counted so far and stop it.
  const pauseQualifyingClock = useCallback(() => {
    const active = activeRef.current;
    if (!active || active.runningSince === null) return;

    active.qualifyingMs += Date.now() - active.runningSince;
    active.runningSince = null;
    clearTimer();
  }, []);

  // Start (or resume) the qualifying clock and arm the crossing timer for the remainder.
  const resumeQualifyingClock = useCallback(
    (durationSeconds?: number | null) => {
      const active = activeRef.current;
      if (!active || active.runningSince !== null) return;
      if (reportedRef.current.has(active.content.id)) return;

      active.runningSince = Date.now();

      const remaining = thresholdFor(active.content, durationSeconds) - active.qualifyingMs;
      if (remaining <= 0) {
        report(active, false);
        return;
      }

      active.timer = window.setTimeout(() => {
        const current = activeRef.current;
        if (current) report(current, false);
      }, remaining);
    },
    [report],
  );

  // Called when an item becomes the focused one.
  const onItemFocused = useCallback(
    (content: TContent, durationSeconds?: number | null) => {
      clearTimer();

      activeRef.current = {
        content,
        focusedAt: Date.now(),
        qualifyingMs: 0,
        runningSince: null,
        timer: null,
      };

      // Articles and posts start counting immediately — being on screen IS the
      // engagement. Video waits for a play event.
      if (content.type !== 'video') resumeQualifyingClock(durationSeconds);
    },
    [resumeQualifyingClock],
  );

  // Called when the user moves off an item. Anything below the fast-swipe bar is recorded
  // as a swipe-away so the backend can tell disinterest from a real short view.
  const onItemBlurred = useCallback(
    (content: TContent) => {
      const active = activeRef.current;
      if (!active || active.content.id !== content.id) return;

      pauseQualifyingClock();
      const onScreenMs = Date.now() - active.focusedAt;
      activeRef.current = null;

      if (onScreenMs < FAST_SWIPE_MS) report(active, true);
    },
    [pauseQualifyingClock, report],
  );

  // Wired to the player: the video's own play/pause drives the qualifying clock.
  const onPlaybackStateChange = useCallback(
    (content: TContent, isPlaying: boolean, durationSeconds?: number | null) => {
      const active = activeRef.current;
      if (!active || active.content.id !== content.id) return;

      if (isPlaying) resumeQualifyingClock(durationSeconds);
      else pauseQualifyingClock();
    },
    [pauseQualifyingClock, resumeQualifyingClock],
  );

  // Completion percentage, captured for whichever source can measure it.
  const onPlaybackProgress = useCallback((content: TContent, currentTime: number, duration: number) => {
    const active = activeRef.current;
    if (!active || active.content.id !== content.id || duration <= 0) return;

    active.completionPct = Math.min(100, (currentTime / duration) * 100);
  }, []);

  useEffect(() => clearTimer, []);

  return { onItemFocused, onItemBlurred, onPlaybackStateChange, onPlaybackProgress, sessionId: sessionIdRef.current };
};

export default useFocusedViewTracker;
